import React, { useEffect, useState } from 'react'
import { Box, Button, Checkbox, Chip, CircularProgress, Grid, IconButton, InputAdornment, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { Close, Search, UploadFile, OpenInNew, CalendarMonth, TableView, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import styles from '../../component/addCategoryForm/addCategoryForm.module.css';
import NestedUserHeader from '../../component/reusable/NestedUserHeader';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../utils/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import ProductTable from '../../component/scheduleComps/ScheduleSearchTable';
import { getAllUsersThunk } from '../../redux/customer/customerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { setschedulerefresh } from '../../redux/schedulecartslice';
import { useNavigate } from 'react-router-dom';
import DeliveryAddressPopup from '../../component/scheduleComps/DeliveryAddressPopup';

const FONT = `'Public Sans', sans-serif`;
const PRIMARY = '#512DA8';
const BORDER = '#E0E2E7';
const MUTED = '#667085';

const cardSx = {
    p: { xs: 2, md: 3 },
    borderRadius: '14px',
    border: `1px solid ${BORDER}`,
    boxShadow: '0 4px 14px rgba(16, 24, 40, 0.04)',
    fontFamily: FONT,
    mb: 2.5,
};

const sectionTitleSx = { fontFamily: FONT, fontWeight: 700, fontSize: '16px', color: '#1D1F2C' };
const sectionHintSx = { fontFamily: FONT, fontSize: '13px', color: MUTED, mt: 0.25 };

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        background: '#FAFAFC',
        fontFamily: FONT,
        fontSize: '14px',
        '& fieldset': { borderColor: '#D0D5DD' },
        '&:hover fieldset': { borderColor: '#B9B3D6' },
        '&.Mui-focused': { background: '#FFFFFF', boxShadow: '0 0 0 3px rgba(81, 45, 168, 0.12)' },
        '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
    },
    '& .MuiInputBase-input': { fontFamily: FONT, fontSize: '14px' },
};

const labelSx = { fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.75, display: 'block' };

const primaryBtnSx = {
    textTransform: 'none',
    fontFamily: FONT,
    fontWeight: 600,
    borderRadius: '8px',
    background: PRIMARY,
    boxShadow: '0 2px 8px rgba(81, 45, 168, 0.3)',
    '&:hover': { background: '#45259A' },
};
const outlineBtnSx = {
    textTransform: 'none',
    fontFamily: FONT,
    fontWeight: 600,
    borderRadius: '8px',
    color: PRIMARY,
    borderColor: PRIMARY,
    '&:hover': { borderColor: PRIMARY, background: '#F0EEFF' },
};

const Field = ({ label, optional, error, children }) => (
    <Box>
        <Typography component="label" sx={labelSx}>
            {label} {optional && <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span>}
        </Typography>
        {children}
        {error && <Typography sx={{ color: '#D92D20', fontSize: '12px', mt: 0.5, fontFamily: FONT }}>{error}</Typography>}
    </Box>
);

const NewSchedule = () => {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [search, setsearch] = useState('')
    const [searched, setSearched] = useState(false)
    const [selectedproducts, setselectedProducts] = useState([])
    const [quantityErrors, setQuantityErrors] = useState({});
    const [totalQuantityErrors, setTotalQuantityErrors] = useState({});
    const [editingProductId, setEditingProductId] = useState(null);
    const { allUsers } = useSelector((state) => state.customer);
    const [editingTotalProductId, setEditingTotalProductId] = useState(null);
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [open, setOpen] = useState(false);
    const [shippingAddress, selectShippingAddress] = useState(null)
    const [fileName, setFileName] = useState('')
    // Optional: send the PO to a vendor right after the schedule is created
    const [submitting, setSubmitting] = useState(false)

    const formik = useFormik({
        initialValues: {
            purchaseOrderUri: '',
            poNumber: '',
            period: '',
            scheduleDate: '',
            remarks: '',
            userId: ''
        },
        validationSchema: Yup.object({
            // The PO file is optional: a PO number alone is enough
            purchaseOrderUri: Yup.string().url('Please upload a valid file'),
            poNumber: Yup.string().trim().max(60, 'PO number is too long'),
            // Optional: empty stays empty, a value must be a positive number
            period: Yup.number()
                .transform((value, originalValue) => (originalValue === '' || originalValue === null ? null : value))
                .nullable()
                .typeError('Period must be a number')
                .positive('Period must be a positive number'),
            scheduleDate: Yup.date()
                .typeError('Invalid date format')
                .required('Schedule Date is required'),
            remarks: Yup.string(),

            userId: Yup.string().required('Customer or vendor is required'),
        }),
        onSubmit: async (values) => {
            const address = allUsers?.find(i => i?._id === values.userId)
            selectShippingAddress(address)
            setOpen(true)
        },
    });

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        try {
            if (file) {
                setLoading(true);
                const body = {
                    key: `${Date.now()}_${file.name}`,
                    fileType: file.type,
                    fileName: file.name,
                };

                const { data, status } = await api.getPutSignedUrl(body);

                if (status === 200) {
                    await axios.put(data.data?.preSigned, file, {
                        headers: {
                            'Content-Type': file.type,
                        },
                    });
                    formik.setFieldValue('purchaseOrderUri', data?.data?.url);
                    formik.setFieldTouched('purchaseOrderUri', true, false);
                    setFileName(file.name);
                    toast.success('PO uploaded');
                }
            }
        } catch (err) {
            console.error(err);
            toast.error('Error uploading file');
        } finally {
            setLoading(false);
            // Lets the same file be chosen again after removing it
            e.target.value = '';
        }
    };

    const removeFile = () => {
        formik.setFieldValue('purchaseOrderUri', '');
        setFileName('');
    };

    // ---- Bulk upload: Excel/CSV with Part Number, Total Qty, Schedule Qty, Unit Price ----
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
    // Bulk-upload rows whose part number is not in the catalog; shown as "Not available" and never submitted
    const [unavailableParts, setUnavailableParts] = useState([]);

    const BULK_COLUMNS = {
        partNumber: ['partnumber', 'part', 'partno', 'mpn', 'productnumber', 'product'],
        manufacturer: ['manufacturer', 'manufacture', 'mfr', 'mfg', 'brand', 'make'],
        totalQty: ['totalqty', 'totalquantity', 'total', 'qty', 'quantity'],
        scheduleQty: ['scheduleqty', 'schedulequantity', 'schedule', 'monthlyqty', 'monthly'],
        unitPrice: ['unitprice', 'price', 'rate'],
    };
    const normalizeHeader = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

    const downloadTemplate = () => {
        const sheet = XLSX.utils.aoa_to_sheet([
            ['Part Number', 'Manufacturer', 'Total Qty', 'Schedule Qty', 'Unit Price'],
            ['0805B104K500CT', 'Walsin', 4000, 1000, 0.6],
            ['HEF4051BT,653', 'Nexperia', 500, 100, 12.5],
        ]);
        sheet['!cols'] = [{ wch: 24 }, { wch: 18 }, { wch: 12 }, { wch: 14 }, { wch: 12 }];
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Schedule');
        XLSX.writeFile(book, 'schedule-bulk-upload-template.xlsx');
    };

    // Reads the rows of the first sheet into { partNumber, totalQty, scheduleQty, unitPrice }
    const parseBulkFile = async (file) => {
        const data = await file.arrayBuffer();
        const book = XLSX.read(data, { type: 'array' });
        const sheet = book.Sheets[book.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }).filter((r) => r.some((c) => String(c).trim() !== ''));
        if (!rows.length) return [];

        // Header row = first row that names a part column; otherwise fixed column order
        const headerIndex = rows.findIndex((r) => r.some((c) => BULK_COLUMNS.partNumber.includes(normalizeHeader(c))));
        let columnIndex = { partNumber: 0, manufacturer: 1, totalQty: 2, scheduleQty: 3, unitPrice: 4 };
        if (headerIndex !== -1) {
            const header = rows[headerIndex].map(normalizeHeader);
            columnIndex = {};
            Object.entries(BULK_COLUMNS).forEach(([field, names]) => {
                const idx = header.findIndex((h) => names.includes(h));
                if (idx !== -1) columnIndex[field] = idx;
            });
        }
        const toNumber = (v) => {
            const n = Number(String(v ?? '').replace(/[₹,\s]/g, ''));
            return Number.isFinite(n) ? n : 0;
        };
        return rows
            .slice(headerIndex === -1 ? 0 : headerIndex + 1)
            .map((r) => ({
                partNumber: String(r[columnIndex.partNumber] ?? '').trim(),
                manufacturer: columnIndex.manufacturer === undefined ? '' : String(r[columnIndex.manufacturer] ?? '').trim(),
                totalQty: toNumber(r[columnIndex.totalQty]),
                scheduleQty: toNumber(r[columnIndex.scheduleQty]),
                unitPrice: toNumber(r[columnIndex.unitPrice]),
            }))
            .filter((r) => r.partNumber);
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
            toast.error('Please choose an Excel (.xlsx/.xls) or CSV file');
            return;
        }

        setBulkLoading(true);
        try {
            const entries = await parseBulkFile(file);
            if (!entries.length) {
                toast.error('No rows found. Columns: Part Number, Manufacturer, Total Qty, Schedule Qty, Unit Price');
                return;
            }
            setBulkProgress({ done: 0, total: entries.length });

            const notFound = [];
            const added = [];
            const adjusted = [];
            // All parts are looked up in one request (exact matches only), so a
            // 100-row file takes one round trip instead of one search per row
            const { data } = await api.bulkPartLookup(entries.map((entry) => entry.partNumber));
            const foundMap = data?.data?.found || {};
            {
                const results = entries.map((entry) => ({ entry, product: foundMap[entry.partNumber] || null }));
                results.forEach(({ entry, product }) => {
                    if (!product) {
                        notFound.push(entry);
                        return;
                    }
                    const minMultiplier = product?.inventory?.minimumMultiplier || 1;
                    // Quantities must be multiples of the SPQ, so round the file's numbers up to the next multiple
                    const roundUp = (n) => Math.max(minMultiplier, Math.ceil((Number(n) || 0) / minMultiplier) * minMultiplier);
                    const totalQty = roundUp(entry.totalQty || entry.scheduleQty || minMultiplier);
                    const scheduleQty = Math.min(totalQty, roundUp(entry.scheduleQty || totalQty));
                    if (totalQty !== (entry.totalQty || entry.scheduleQty || minMultiplier) || scheduleQty !== (entry.scheduleQty || totalQty)) {
                        adjusted.push(`${entry.partNumber} (SPQ ${minMultiplier})`);
                    }
                    added.push({
                        productId: product._id,
                        quantity: scheduleQty,
                        totalQty,
                        productDetails: product,
                        previousValidQuantity: scheduleQty,
                        previousValidTotalQty: totalQty,
                        ...(entry.unitPrice > 0 ? { unitPrice: entry.unitPrice } : {}),
                    });
                });
                setBulkProgress({ done: entries.length, total: entries.length });
            }

            if (added.length) {
                setselectedProducts((prev) => {
                    const next = [...prev];
                    added.forEach((row) => {
                        const idx = next.findIndex((p) => p.productId === row.productId);
                        if (idx >= 0) next[idx] = { ...next[idx], ...row };
                        else next.push(row);
                    });
                    return next;
                });
                toast.success(`${added.length} product(s) added to the schedule from ${file.name}`);
            }
            if (adjusted.length) {
                toast.info(`Quantities rounded up to the SPQ for: ${adjusted.slice(0, 5).join(', ')}${adjusted.length > 5 ? ` +${adjusted.length - 5} more` : ''}`, { autoClose: 10000 });
            }
            if (notFound.length) {
                // Keep them visible in the table as "Not available" so the admin can see what was skipped
                setUnavailableParts((prev) => {
                    const next = [...prev];
                    notFound.forEach((entry) => {
                        const key = String(entry.partNumber).toUpperCase();
                        const idx = next.findIndex((p) => String(p.partNumber).toUpperCase() === key);
                        const row = { partNumber: entry.partNumber, manufacturer: entry.manufacturer || '', totalQty: entry.totalQty || '', scheduleQty: entry.scheduleQty || '', unitPrice: entry.unitPrice || '' };
                        if (idx >= 0) next[idx] = row;
                        else next.push(row);
                    });
                    return next;
                });
                const names = notFound.map((entry) => entry.partNumber);
                toast.warn(`${names.length} part(s) not available in the catalog: ${names.slice(0, 5).join(', ')}${names.length > 5 ? ` +${names.length - 5} more` : ''}`, { autoClose: 10000 });
            }
        } catch (err) {
            console.error(err);
            toast.error('Could not read this file');
        } finally {
            setBulkLoading(false);
            setBulkProgress({ done: 0, total: 0 });
        }
    };

    const searchProduct = async (e) => {
        if (e) e.preventDefault()
        const term = search.trim()
        if (!term) {
            toast.info('Type a part number or description to search')
            return
        }
        try {
            setSearchLoading(true)
            // Part numbers can contain "#", "/" or "&", so the term must be encoded
            const { data } = await api.getExactSearch(`?search=${encodeURIComponent(term)}`)
            setProducts(Array.isArray(data?.data) ? data.data : [])
            setSearched(true)
        } catch (err) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Search failed, please try again')
        } finally {
            setSearchLoading(false)
        }
    }

    const clearSearch = () => {
        setsearch('');
        setProducts([]);
        setSearched(false);
    };

    // Results appear while typing (after 2 characters), without pressing Search
    useEffect(() => {
        const term = search.trim();
        if (term.length < 2) return undefined;
        const timer = setTimeout(async () => {
            try {
                setSearchLoading(true);
                const { data } = await api.getExactSearch(`?search=${encodeURIComponent(term)}`);
                setProducts(Array.isArray(data?.data) ? data.data : []);
                setSearched(true);
            } catch (err) {
                console.log(err);
            } finally {
                setSearchLoading(false);
            }
        }, 450);
        return () => clearTimeout(timer);
    }, [search]);

    const handleRemoveProduct = (productId) => {
        setselectedProducts(prev =>
            prev.filter(item => item.productId !== productId)
        );
        // Clear errors for removed product
        setQuantityErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[productId];
            return newErrors;
        });
        setTotalQuantityErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[productId];
            return newErrors;
        });
    }

    // Validate quantity against minimumMultiplier and total quantity
    const validateQuantity = (productId, value, totalQty) => {
        const product = selectedproducts.find(p => p.productId === productId);
        const minMultiplier = product?.productDetails?.inventory?.minimumMultiplier || 1;
        const numValue = Number(value);
        const numTotalQty = Number(totalQty);

        if (value === '') {
            setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
            return false;
        }

        if (isNaN(numValue)) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Please enter a valid number' }));
            return false;
        }

        if (numValue <= 0) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Quantity must be greater than 0' }));
            return false;
        }

        if (numValue % minMultiplier !== 0) {
            setQuantityErrors(prev => ({
                ...prev,
                [productId]: `Quantity must be in multiples of ${minMultiplier}`
            }));
            return false;
        }

        if (numValue > numTotalQty) {
            setQuantityErrors(prev => ({
                ...prev,
                [productId]: `Cannot exceed total quantity (${totalQty})`
            }));
            return false;
        }

        setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
        return true;
    };

    // Validate total quantity against minimumMultiplier
    const validateTotalQuantity = (productId, value) => {
        const product = selectedproducts.find(p => p.productId === productId);
        const minMultiplier = product?.productDetails?.inventory?.minimumMultiplier || 1;
        const numValue = Number(value);

        if (value === '') {
            setTotalQuantityErrors(prev => ({ ...prev, [productId]: '' }));
            return false;
        }

        if (isNaN(numValue)) {
            setTotalQuantityErrors(prev => ({ ...prev, [productId]: 'Please enter a valid number' }));
            return false;
        }

        if (numValue <= 0) {
            setTotalQuantityErrors(prev => ({ ...prev, [productId]: 'Quantity must be greater than 0' }));
            return false;
        }

        if (numValue % minMultiplier !== 0) {
            setTotalQuantityErrors(prev => ({
                ...prev,
                [productId]: `Quantity must be in multiples of ${minMultiplier}`
            }));
            return false;
        }

        setTotalQuantityErrors(prev => ({ ...prev, [productId]: '' }));
        return true;
    };

    const handleQuantityChange = (productId, newQuantity, totalQty) => {
        // Update the state immediately
        setselectedProducts(prev =>
            prev.map(item => {
                if (item.productId === productId) {
                    return {
                        ...item,
                        quantity: newQuantity === '' ? '' : parseInt(newQuantity, 10) || 0
                    };
                }
                return item;
            })
        );

        // Then validate
        validateQuantity(productId, newQuantity, totalQty);
    };

    const handleTotalQuantityChange = (productId, newQuantity, quantity) => {
        // Update the state immediately
        setselectedProducts(prev =>
            prev.map(item => {
                if (item.productId === productId) {
                    return {
                        ...item,
                        totalQty: newQuantity === '' ? '' : parseInt(newQuantity, 10) || 0,
                        // Ensure quantity doesn't exceed the new total quantity
                        quantity: Math.min(item.quantity, newQuantity === '' ? Infinity : parseInt(newQuantity, 10) || 0)
                    };
                }
                return item;
            })
        );

        // Then validate
        validateTotalQuantity(productId, newQuantity);

        // Also validate if existing quantity is still valid with new total
        const product = selectedproducts.find(p => p.productId === productId);
        if (product) {
            validateQuantity(productId, product.quantity, newQuantity);
        }
    };

    const handleQuantityBlur = (productId, isTotal = false) => {
        if (isTotal) {
            setEditingTotalProductId(null);
        } else {
            setEditingProductId(null);
        }

        const product = selectedproducts.find(p => p.productId === productId);
        if (product) {
            if (isTotal) {
                const isValid = validateTotalQuantity(productId, product.totalQty);
                if (!isValid) {
                    // Reset to last valid total quantity
                    setselectedProducts(prev =>
                        prev.map(p => {
                            if (p.productId === productId) {
                                return { ...p, totalQty: p.previousValidTotalQty || 1 };
                            }
                            return p;
                        })
                    );
                }
            } else {
                const isValid = validateQuantity(productId, product.quantity, product.totalQty);
                if (!isValid) {
                    // Reset to last valid quantity
                    setselectedProducts(prev =>
                        prev.map(p => {
                            if (p.productId === productId) {
                                return { ...p, quantity: p.previousValidQuantity || 1 };
                            }
                            return p;
                        })
                    );
                }
            }
        }
    };

    const handleQuantityFocus = (productId, isTotal = false) => {
        // Store the current valid quantity before editing
        setselectedProducts(prev =>
            prev.map(item => {
                if (item.productId === productId) {
                    return {
                        ...item,
                        previousValidQuantity: isTotal ? item.previousValidQuantity : item.quantity,
                        previousValidTotalQty: isTotal ? item.totalQty : item.previousValidTotalQty
                    };
                }
                return item;
            })
        );

        if (isTotal) {
            setEditingTotalProductId(productId);
        } else {
            setEditingProductId(productId);
        }
    };

    const isQuantityValid = (productId) => {
        const product = selectedproducts.find(p => p.productId === productId);
        if (!product) return false;

        const quantity = product.quantity;
        const totalQty = product.totalQty;
        const minMultiplier = product.productDetails?.inventory?.minimumMultiplier || 1;

        return (
            quantity > 0 &&
            totalQty > 0 &&
            quantity % minMultiplier === 0 &&
            totalQty % minMultiplier === 0 &&
            quantity <= totalQty
        );
    };

    const validateBeforeSubmit = () => {
        if (selectedproducts.length === 0 && unavailableParts.length === 0) {
            toast.error('Please add at least one product: enter the quantity and click "+ Add to schedule" (or tick the box)');
            return false;
        }

        const hasInvalidQuantity = selectedproducts.some(item => !isQuantityValid(item.productId));
        if (hasInvalidQuantity) {
            toast.error('Please enter valid quantities (multiples of SPQ) for all items');
            return false;
        }

        return true;
    };

    const handleSubmit = () => {
        if (!validateBeforeSubmit()) {
            return;
        }
        // Proceed with form submission
        formik.handleSubmit();
    };

    const handleSaveSelection = async () => {
        if (submitting) return;
        const day = new Date(formik.values.scheduleDate).getDate();
        const formData = {
            ...formik.values,
            poNumber: formik.values.poNumber?.trim() || undefined,
            // Optional fields are left out when empty
            period: formik.values.period === '' ? undefined : Number(formik.values.period),
            remarks: formik.values.remarks?.trim() || undefined,
            products: [
                ...selectedproducts.map((item) => ({
                    productId: item.productId,
                    quantity: item.quantity,
                    totalQty: item.totalQty,
                    // Typed price wins over the catalog price breaks
                    ...(item.unitPrice !== undefined && item.unitPrice !== '' && Number(item.unitPrice) > 0 ? { unitPrice: Number(item.unitPrice) } : {}),
                })),
                // Parts not in the catalog go on the schedule by part number, as uploaded
                ...unavailableParts.map((part) => ({
                    productId: null,
                    partNumber: part.partNumber,
                    manufacturer: part.manufacturer || '',
                    quantity: Number(part.scheduleQty) || Number(part.totalQty) || 1,
                    totalQty: Number(part.totalQty) || Number(part.scheduleQty) || 1,
                    ...(Number(part.unitPrice) > 0 ? { unitPrice: Number(part.unitPrice) } : {}),
                })),
            ],
            scheduleDate: day,
            // Full date, so the pages can show "30 Oct 2026" instead of just the day
            scheduleStartDate: formik.values.scheduleDate,
            shippingAddress: selectedAddress
        };

        setSubmitting(true);
        try {
            const res = await api.createScheduleenquiry(formData);
            const scheduleId = res?.data?.data?._id;
            dispatch(setschedulerefresh());
            const poLabel = formData.poNumber ? `PO ${formData.poNumber}` : (scheduleId ? `#${scheduleId}` : '');
            toast.success(poLabel ? `Schedule ${poLabel} created` : 'Schedule created');

            setOpen(false);
            navigate('/schedule/scheduleEnquires');
        } catch (err) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Could not create the schedule');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddtoSchedule = (productId, quantity, productDetails) => {
        if (!quantity || isNaN(quantity)) {
            toast.error('Please enter a valid quantity');
            return;
        }

        const numQuantity = Number(quantity);
        const minMultiplier = productDetails?.inventory?.minimumMultiplier || 1;

        if (numQuantity % minMultiplier !== 0) {
            toast.error(`Quantity must be in multiples of ${minMultiplier}`);
            return;
        }

        setselectedProducts(prev => {
            const existingIndex = prev.findIndex(item => item.productId === productId);

            if (existingIndex >= 0) {
                // Update existing product
                const updatedProducts = [...prev];
                updatedProducts[existingIndex] = {
                    ...updatedProducts[existingIndex],
                    quantity: numQuantity,
                    totalQty: numQuantity, // Set both to same value initially
                    productDetails
                };
                return updatedProducts;
            } else {
                // Add new product
                return [...prev, {
                    productId,
                    quantity: numQuantity,
                    totalQty: numQuantity,
                    productDetails,
                    previousValidQuantity: numQuantity,
                    previousValidTotalQty: numQuantity
                }];
            }
        });
        toast.success('Added to the schedule');
    };

    useEffect(() => {
        dispatch(getAllUsersThunk())
    }, [dispatch])

    const byCompany = (a, b) => (a?.companyName || a?.fullName || '').localeCompare(b?.companyName || b?.fullName || '');
    // A schedule can be for a customer or a vendor, so both are offered in one list
    const customerOptions = (allUsers || []).filter((u) => u?.userType !== 'ACCOUNT_USER').sort(byCompany);

    const hasInvalidRows = selectedproducts.some(item => !isQuantityValid(item.productId));
    const invalidRowNames = selectedproducts.filter(item => !isQuantityValid(item.productId)).map(item => item.productDetails?.productOverview?.productNumber || 'part');

    // Rounds every invalid row up to its SPQ and keeps schedule qty within total qty
    const fixInvalidQuantities = () => {
        setselectedProducts((prev) => prev.map((item) => {
            if (isQuantityValid(item.productId)) return item;
            const spq = item.productDetails?.inventory?.minimumMultiplier || 1;
            const roundUp = (n) => Math.max(spq, Math.ceil((Number(n) || 0) / spq) * spq);
            const totalQty = roundUp(item.totalQty || item.quantity || spq);
            const quantity = Math.min(totalQty, roundUp(item.quantity || totalQty));
            return { ...item, totalQty, quantity, previousValidTotalQty: totalQty, previousValidQuantity: quantity };
        }));
        setTotalQuantityErrors({});
        setQuantityErrors({});
        toast.success('Quantities rounded up to the SPQ of each product');
    };

    // Unit price from the product's price breaks for the scheduled quantity
    // (same rule the backend uses); 0 when the part has no price yet.
    const unitPriceFor = (item) => {
        const table = Array.isArray(item?.productDetails?.quantityTable) ? item.productDetails.quantityTable : [];
        if (!table.length) return 0;
        const qty = Number(item?.quantity) || 0;
        const sorted = [...table].sort((a, b) => Number(a?.breakQty) - Number(b?.breakQty));
        const row = sorted.find((entry) => qty <= Number(entry?.breakQty)) || sorted[sorted.length - 1];
        return Number(row?.unitPrice) || 0;
    };
    // The admin can type a unit price per row; it overrides the catalog price
    const effectivePrice = (item) => {
        const manual = item?.unitPrice;
        if (manual !== undefined && manual !== null && manual !== '') return Number(manual) || 0;
        return unitPriceFor(item);
    };
    const setManualPrice = (productId, value) => {
        setselectedProducts((prev) => prev.map((p) => (p.productId === productId ? { ...p, unitPrice: value } : p)));
    };
    const money = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
    const scheduleTotal = selectedproducts.reduce((sum, item) => sum + effectivePrice(item) * (Number(item.quantity) || 0), 0);
    const unpricedCount = selectedproducts.filter((item) => effectivePrice(item) <= 0).length;

    return (
        <>
            <NestedUserHeader
                index="New schedule"
                root="Dashboard"
                buttonText={'Add Subcategory'}
                actions={
                    <>
                        {/* Bulk upload of parts with quantities and prices */}
                        <input
                            type="file"
                            style={{ display: 'none' }}
                            id="bulkScheduleUpload"
                            accept=".xlsx, .xls, .csv"
                            onChange={handleBulkUpload}
                        />
                        <Button
                            component="label"
                            htmlFor="bulkScheduleUpload"
                            variant="outlined"
                            disabled={bulkLoading}
                            startIcon={bulkLoading ? <CircularProgress size={16} /> : <TableView />}
                            title="Excel/CSV with Part Number, Total Qty, Schedule Qty, Unit Price"
                            sx={{ ...outlineBtnSx, height: 40, px: 2, whiteSpace: 'nowrap' }}
                        >
                            {bulkLoading ? `Adding ${bulkProgress.done}/${bulkProgress.total}…` : 'Bulk upload'}
                        </Button>
                        <input
                            type="file"
                            style={{ display: 'none' }}
                            id="fileUpload"
                            accept=".xlsx, .xls, .csv, .pdf"
                            onChange={handleFileUpload}
                        />
                        {formik.values.purchaseOrderUri ? (
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 40, px: 1.5, borderRadius: '8px', border: '1px solid #ABEFC6', background: '#ECFDF3', maxWidth: 360 }}>
                                <UploadFile sx={{ fontSize: 18, color: '#067647' }} />
                                <Typography
                                    component="a"
                                    href={formik.values.purchaseOrderUri}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={fileName || formik.values.purchaseOrderUri}
                                    sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: '#067647', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    PO: {fileName || decodeURIComponent(formik.values.purchaseOrderUri.split('/').pop() || 'Uploaded file')}
                                    <OpenInNew sx={{ fontSize: 13 }} />
                                </Typography>
                                <IconButton size="small" onClick={removeFile} aria-label="remove file">
                                    <Close sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Stack>
                        ) : (
                            <Button
                                component="label"
                                htmlFor="fileUpload"
                                variant="contained"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <UploadFile />}
                                sx={{ ...primaryBtnSx, height: 40, px: 2.25, whiteSpace: 'nowrap' }}
                            >
                                {loading ? 'Uploading…' : 'Upload PO'}
                            </Button>
                        )}
                    </>
                }
            />

            <Box sx={{ px: { xs: 1.5, md: 2.5 }, pb: 6 }}>
                {/* Step 1: products */}
                <Paper sx={cardSx}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
                        <Box>
                            <Typography sx={sectionTitleSx}>1. Add products</Typography>
                            <Typography sx={sectionHintSx}>
                                Search by part number or description, set the quantity and add it to the schedule. Or use <b>Bulk upload</b> (top right) with an Excel/CSV of Part Number, Manufacturer, Total Qty, Schedule Qty and Unit Price.{' '}
                                <Button size="small" onClick={downloadTemplate} startIcon={<Download sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontFamily: FONT, fontSize: '12.5px', color: PRIMARY, p: 0, minWidth: 0, verticalAlign: 'baseline' }}>
                                    Download template
                                </Button>
                            </Typography>
                        </Box>
                        {selectedproducts.length > 0 && (
                            <Chip label={`${selectedproducts.length} product(s) selected`} sx={{ background: '#F0EEFF', color: PRIMARY, fontWeight: 600, fontFamily: FONT }} />
                        )}
                    </Stack>

                    <Box component="form" onSubmit={searchProduct} sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            placeholder="Search part number, e.g. 0805B104K500CT"
                            value={search}
                            onChange={(e) => setsearch(e.target.value)}
                            sx={{ ...fieldSx, flex: '1 1 320px', maxWidth: 560 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: MUTED, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearSearch} aria-label="clear search">
                                            <Close sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={searchLoading}
                            startIcon={searchLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Search />}
                            sx={{ ...primaryBtnSx, px: 2.5, height: 40 }}
                        >
                            {searchLoading ? 'Searching…' : 'Search'}
                        </Button>
                    </Box>

                    {products?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ ...sectionHintSx, mb: 1 }}>
                                {products.length} result(s){products.length >= 100 ? ' (showing the first 100, refine the search for more)' : ''}
                            </Typography>
                            <ProductTable
                                products={products}
                                handleAddtoSchedule={handleAddtoSchedule}
                                handleRemoveFromSchedule={handleRemoveProduct}
                                selectedIds={selectedproducts.map((p) => p.productId)}
                            />
                        </Box>
                    )}
                    {searched && !searchLoading && products?.length === 0 && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: '10px', background: '#FFFAEB', border: '1px solid #FEDF89' }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: '13.5px', color: '#B54708' }}>
                                No products found for "{search.trim()}". Try a part number, manufacturer part number or a word from the description.
                            </Typography>
                        </Box>
                    )}

                    {/* Selected Products List */}
                    {(selectedproducts.length > 0 || unavailableParts.length > 0) && (
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{ ...sectionTitleSx, fontSize: '15px', mb: 1 }}>Selected products</Typography>
                            <Box
                                sx={{
                                    overflowX: 'auto',
                                    borderRadius: '10px',
                                    border: `1px solid ${BORDER}`,
                                    '& table': { tableLayout: 'fixed', borderCollapse: 'collapse' },
                                    '& th': { fontFamily: FONT, fontSize: '12px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', background: '#F9FAFB', padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: `1px solid ${BORDER}` },
                                    '& td': { fontFamily: FONT, fontSize: '14px', color: '#1D1F2C', padding: '12px 14px', verticalAlign: 'middle', borderBottom: `1px solid ${BORDER}` },
                                    '& tbody tr:last-of-type td': { borderBottom: 'none' },
                                    '& th:nth-of-type(2), & td:nth-of-type(2)': { textAlign: 'center' },
                                    '& th:nth-of-type(5), & td:nth-of-type(5), & th:nth-of-type(6), & td:nth-of-type(6)': { textAlign: 'right' },
                                    '& th:last-of-type, & td:last-of-type': { textAlign: 'right' },
                                    '& input[type="number"]': { width: '100%', maxWidth: 120, height: 36, padding: '0 10px', borderRadius: '8px', border: '1px solid #D0D5DD', background: '#FAFAFC', fontFamily: FONT, fontSize: '14px', boxSizing: 'border-box', margin: 0 },
                                    '& input[type="number"]:focus': { outline: 'none', borderColor: PRIMARY, background: '#fff', boxShadow: '0 0 0 3px rgba(81, 45, 168, 0.12)' },
                                }}
                            >
                                <table style={{ margin: 0, width: '100%' }}>
                                    <colgroup>
                                        <col style={{ width: '26%' }} />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '14%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>SPQ</th>
                                            <th>Total qty</th>
                                            <th>Schedule qty</th>
                                            <th>Unit price</th>
                                            <th>Line total</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedproducts.map((item, index) => {
                                            const minMultiplier = item?.productDetails?.inventory?.minimumMultiplier || 1;
                                            return (
                                                <tr key={item.productId || index}>
                                                    <td>
                                                        <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{item.productDetails?.productOverview?.productNumber}</div>
                                                        <div style={{ fontSize: 12, color: MUTED }}>
                                                            {item.productDetails?.productOverview?.manufacturer?.[0]?.name || ''}
                                                        </div>
                                                    </td>
                                                    <td>{minMultiplier}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={item.totalQty}
                                                            onChange={(e) => handleTotalQuantityChange(item.productId, e.target.value, item.quantity)}
                                                            onBlur={() => handleQuantityBlur(item.productId, true)}
                                                            onFocus={() => handleQuantityFocus(item.productId, true)}
                                                            min="1"
                                                            step={minMultiplier}
                                                            className={styles.quantityInput}
                                                        />
                                                        {totalQuantityErrors[item.productId] && (
                                                            <div className={styles.errorText}>
                                                                {totalQuantityErrors[item.productId]}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleQuantityChange(item.productId, e.target.value, item.totalQty)}
                                                            onBlur={() => handleQuantityBlur(item.productId)}
                                                            onFocus={() => handleQuantityFocus(item.productId)}
                                                            min="1"
                                                            max={item.totalQty}
                                                            step={minMultiplier}
                                                            className={styles.quantityInput}
                                                        />
                                                        {quantityErrors[item.productId] && (
                                                            <div className={styles.errorText}>
                                                                {quantityErrors[item.productId]}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                                            <span style={{ color: MUTED, fontSize: 13 }}>₹</span>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.001"
                                                                placeholder={unitPriceFor(item) > 0 ? String(unitPriceFor(item)) : 'Enter price'}
                                                                value={item.unitPrice ?? (unitPriceFor(item) > 0 ? unitPriceFor(item) : '')}
                                                                onChange={(e) => setManualPrice(item.productId, e.target.value)}
                                                                style={{ maxWidth: 110, textAlign: 'right' }}
                                                            />
                                                        </div>
                                                        {unitPriceFor(item) > 0 && item.unitPrice !== undefined && item.unitPrice !== '' && Number(item.unitPrice) !== unitPriceFor(item) && (
                                                            <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>Catalog: {money(unitPriceFor(item))}</div>
                                                        )}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                        {effectivePrice(item) > 0 ? money(effectivePrice(item) * (Number(item.quantity) || 0)) : <span style={{ color: MUTED }}>—</span>}
                                                    </td>
                                                    <td>
                                                        <Button size="small" color="error" onClick={() => handleRemoveProduct(item.productId)} sx={{ textTransform: 'none', fontFamily: FONT }}>
                                                            Remove
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {/* Parts from the bulk file that are not in the catalog: shown, never submitted */}
                                        {unavailableParts.map((part) => (
                                            <tr key={`na-${part.partNumber}`} style={{ background: '#FFF7F7' }}>
                                                <td>
                                                    <div style={{ fontWeight: 600, overflowWrap: 'anywhere', color: '#6B7280' }}>{part.partNumber}</div>
                                                    {part.manufacturer && <div style={{ fontSize: 12, color: MUTED }}>{part.manufacturer}</div>}
                                                    <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}>
                                                        Not available in catalog · will be scheduled by part number
                                                    </span>
                                                </td>
                                                <td style={{ color: MUTED }}>—</td>
                                                <td style={{ color: MUTED }}>{part.totalQty || '—'}</td>
                                                <td style={{ color: MUTED }}>{part.scheduleQty || '—'}</td>
                                                <td style={{ color: MUTED, whiteSpace: 'nowrap' }}>{part.unitPrice ? money(Number(part.unitPrice)) : '—'}</td>
                                                <td style={{ color: MUTED }}>—</td>
                                                <td>
                                                    <Button size="small" color="error" onClick={() => setUnavailableParts((prev) => prev.filter((p) => p.partNumber !== part.partNumber))} sx={{ textTransform: 'none', fontFamily: FONT }}>
                                                        Remove
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mt: 1.5 }}>
                                <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: unpricedCount > 0 ? '#B54708' : MUTED }}>
                                    {unpricedCount > 0
                                        ? `${unpricedCount} product(s) have no price yet. Type a unit price in the row to include them in the total.`
                                        : `${selectedproducts.length} product(s) · prices from the catalog price breaks`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, px: 2, py: 1.25, borderRadius: '10px', background: '#F0EEFF', border: '1px solid #E0D7FA' }}>
                                    <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#5B4B8A', whiteSpace: 'nowrap' }}>Schedule total (excl. GST)</Typography>
                                    <Typography sx={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: PRIMARY, whiteSpace: 'nowrap' }}>{money(scheduleTotal)}</Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </Paper>

                {/* Step 2: details */}
                <Paper sx={cardSx}>
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={sectionTitleSx}>2. Schedule details</Typography>
                        <Typography sx={sectionHintSx}>Pick the customer, add the PO and choose the monthly schedule date.</Typography>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Field label="Customer / Vendor (company)" error={formik.touched.userId && formik.errors.userId}>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    name="userId"
                                    value={formik.values.userId}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                    SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                                >
                                    <MenuItem value="" sx={{ fontFamily: FONT, color: MUTED }}>Select customer or vendor</MenuItem>
                                    {customerOptions.map((i) => (
                                        <MenuItem key={i?._id} value={i?._id} sx={{ fontFamily: FONT }}>
                                            <Box>
                                                <Typography sx={{ fontFamily: FONT, fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {i?.companyName || i?.fullName || i?.email}
                                                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', padding: '1px 6px', borderRadius: 999, background: i?.userType === 'VENDOR' ? '#FFF4E5' : '#F0EEFF', color: i?.userType === 'VENDOR' ? '#B54708' : PRIMARY }}>
                                                        {i?.userType === 'VENDOR' ? 'VENDOR' : 'CUSTOMER'}
                                                    </span>
                                                </Typography>
                                                <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: MUTED }}>
                                                    {i?.companyName ? `${i?.fullName || ''}${i?.email ? ` · ${i.email}` : ''}` : 'No company name'}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Field>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Field label="PO Number" error={formik.touched.poNumber && formik.errors.poNumber}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="poNumber"
                                    placeholder="e.g. PO-2026-0142"
                                    value={formik.values.poNumber}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                />
                            </Field>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Field label="Schedule Date" error={formik.touched.scheduleDate && formik.errors.scheduleDate}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="date"
                                    name="scheduleDate"
                                    value={formik.values.scheduleDate}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonth sx={{ color: MUTED, fontSize: 18 }} /></InputAdornment> }}
                                />
                            </Field>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Field label="Months Period" optional error={formik.touched.period && formik.errors.period}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    name="period"
                                    placeholder="e.g. 6"
                                    value={formik.values.period}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                    inputProps={{ min: 1 }}
                                />
                            </Field>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Field label="Project remarks" optional error={formik.touched.remarks && formik.errors.remarks}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="remarks"
                                    placeholder="Anything the team should know"
                                    value={formik.values.remarks}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                />
                            </Field>
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1.5} sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${BORDER}` }}>
                        {hasInvalidRows && !submitting && (
                            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5} sx={{ mr: 'auto' }}>
                                <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: '#B54708' }}>
                                    {invalidRowNames.length} product(s) have quantities that are not multiples of their SPQ: {invalidRowNames.slice(0, 3).join(', ')}{invalidRowNames.length > 3 ? ` +${invalidRowNames.length - 3} more` : ''}.
                                </Typography>
                                <Button size="small" variant="outlined" onClick={fixInvalidQuantities} sx={{ ...outlineBtnSx, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    Fix quantities (round to SPQ)
                                </Button>
                            </Stack>
                        )}
                        <Button variant="outlined" onClick={() => navigate('/schedule/scheduleEnquires')} sx={{ ...outlineBtnSx, px: 2.5 }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={hasInvalidRows || submitting}
                            startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                            sx={{ ...primaryBtnSx, px: 3, whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                            {submitting ? 'Submitting…' : 'Approve & submit'}
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            <DeliveryAddressPopup
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                setOpen={setOpen}
                open={open}
                handleSubmit={handleSaveSelection}
                customer={shippingAddress}
            />
        </>
    )
}

export default NewSchedule
